import { MessageCircle, Search, Sparkles, ThumbsUp } from "lucide-react";
import { useMemo, useState } from "react";
import type { PlaceCategory, PlaceDTO } from "@shared/types";
import { PLACE_CATEGORIES } from "@shared/types";
import { fmtDayShort } from "@/lib/format";
import { CATEGORY, PLACE_STATUS } from "@/lib/meta";
import { useUI } from "@/lib/store";
import type { Bundle, Derived } from "@/lib/trip";
import { Button, Chip, cx, Empty, Input } from "../ui";

type StatusFilter = "all" | "idea" | "want" | "plan" | "visited" | "social";

export function PlacesPanel({ b, d }: { b: Bundle; d: Derived }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [cat, setCat] = useState<PlaceCategory | null>(null);
  const selectPlace = useUI((s) => s.selectPlace);
  const set = useUI((s) => s.set);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = b.places.filter((p) => {
      const inPlan = (d.placeDays.get(p.id)?.length ?? 0) > 0;
      if (status === "plan" && !inPlan) return false;
      if (status === "idea" && (p.status !== "idea" || inPlan)) return false;
      if (status === "want" && p.status !== "want") return false;
      if (status === "visited" && p.status !== "visited") return false;
      if (status === "social" && !(p.votes?.length || p.commentCount)) return false;
      if (cat && p.category !== cat) return false;
      if (s && !`${p.name} ${p.nameLocal ?? ""} ${p.region ?? ""} ${p.description ?? ""} ${p.tags.join(" ")}`.toLowerCase().includes(s)) return false;
      return true;
    });
    // группы по регионам в порядке маршрута (по первому дню)
    const firstDay = (p: PlaceDTO) => d.placeDays.get(p.id)?.slice().sort()[0] ?? "9999";
    const m = new Map<string, PlaceDTO[]>();
    for (const p of list) m.set(p.region ?? "Без региона", [...(m.get(p.region ?? "Без региона") ?? []), p]);
    return [...m.entries()]
      .map(([region, ps]) => ({ region, places: ps.sort((a, z) => firstDay(a).localeCompare(firstDay(z)) || a.name.localeCompare(z.name)), order: ps.map(firstDay).sort()[0] }))
      .sort((a, z) => a.order.localeCompare(z.order));
  }, [b.places, d, q, status, cat]);

  const total = groups.reduce((s, g) => s + g.places.length, 0);
  const cats = PLACE_CATEGORIES.filter((c) => b.places.some((p) => p.category === c));

  return (
    <div className="pb-6">
      {b.trip.myRole !== "viewer" && (
        <Button variant="soft" className="mb-3 w-full" icon={<Sparkles size={16} />} onClick={() => set({ placeDraft: {} })}>
          Новое место — словами или ссылкой
        </Button>
      )}
      <div className="relative mb-2">
        <Search size={16} className="absolute top-3 left-3 text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Поиск среди ${b.places.length} мест`} className="pl-9" />
      </div>
      <div className="no-scrollbar -mx-1 mb-2 flex gap-1.5 overflow-x-auto px-1">
        {(
          [
            ["all", "Все"],
            ["plan", "В плане"],
            ["want", "Хотим"],
            ["idea", "Идеи"],
            ["visited", "Были"],
            ["social", "👍 Обсуждаемые"],
          ] as [StatusFilter, string][]
        ).map(([k, l]) => (
          <Chip key={k} active={status === k} onClick={() => setStatus(k)}>
            {l}
          </Chip>
        ))}
      </div>
      <div className="no-scrollbar -mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1">
        {cats.map((c) => (
          <Chip key={c} active={cat === c} color={CATEGORY[c].color} onClick={() => setCat(cat === c ? null : c)}>
            {CATEGORY[c].emoji} {CATEGORY[c].label}
          </Chip>
        ))}
      </div>
      {!total && <Empty icon="🔎" title="Ничего не найдено">Поменяйте фильтры или добавьте место через поиск на карте.</Empty>}
      {groups.map((g) => (
        <section key={g.region} className="mb-3">
          <h3 className="sticky top-0 z-10 -mx-1 bg-[var(--surface-solid)] px-2 py-1 text-[12px] font-extrabold tracking-wider text-muted uppercase">
            {g.region} <span className="font-semibold">· {g.places.length}</span>
          </h3>
          {g.places.map((p) => {
            const days = d.placeDays.get(p.id) ?? [];
            const di = days[0] ? d.dayIndex.get(days.slice().sort()[0]) : null;
            const ups = p.votes?.filter((v) => v.value > 0).length ?? 0;
            return (
              <button
                key={p.id}
                onClick={() => {
                  selectPlace(p.id);
                  useUI.getState().focusMap({ bounds: [[p.lat, p.lng], [p.lat, p.lng]] });
                }}
                onMouseEnter={() => set({ hoverPlaceId: p.id })}
                onMouseLeave={() => set({ hoverPlaceId: null })}
                className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-[var(--surface-2)]"
              >
                <span
                  className={cx("grid size-9 shrink-0 place-items-center rounded-full border-2 text-[16px]", !days.length && "border-dashed")}
                  style={{ borderColor: di?.color ?? CATEGORY[p.category].color }}
                >
                  {CATEGORY[p.category].emoji}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold">{p.name}</span>
                  <span className="block truncate text-[12px] text-muted">
                    {days.length ? `📅 ${days.sort().map((x) => fmtDayShort(x)).join(", ")}` : PLACE_STATUS[p.status].label}
                    {p.description ? ` · ${p.description}` : ""}
                  </span>
                </span>
                {ups > 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-bold text-jade-600 dark:text-jade-300">
                    <ThumbsUp size={12} /> {ups}
                  </span>
                )}
                {(p.commentCount ?? 0) > 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-bold text-sky-600 dark:text-sky-300">
                    <MessageCircle size={12} /> {p.commentCount}
                  </span>
                )}
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
}
